import { Injectable } from '@angular/core';
import { Point } from '@root/app.interfaces';

@Injectable({
	providedIn: 'root',
})
export class Util {
	private readonly DESKTOP_COEFFS = [-137.34, 185.25, -86.853, 20.085];
	private readonly MOBILE_COEFFS = [-86.177, 90.497, -47.885, 20.144];

	// Simple sort method to ensure the coordinates are in the right order. NOTE: will fail to work correctly with significant warping or rotation
	sortQuad(rawCoords: Point[]): Point[] {
		const pts = [...rawCoords].sort((a, b) => a.y - b.y); // ascending y
		const top = pts.slice(0, 2).sort((a, b) => a.x - b.x); // ascending x -> TL, TR
		const bottom = pts.slice(2).sort((a, b) => a.x - b.x); // ascending x -> BL, BR

		const [topLeft, topRight] = top;
		const [bottomLeft, bottomRight] = bottom;

		return [topLeft, topRight, bottomRight, bottomLeft];
	}

	polynomialHorner = (x: number, coefficients: number[]): number => coefficients.reduce((acc, c) => acc * x + c, 0);

	radiusFromNormDesktop(x: number): number {
		const clamped = Math.min(Math.max(x, 0), 1);
		return this.polynomialHorner(clamped, this.DESKTOP_COEFFS);
	}

	radiusFromNormMobile(x: number): number {
		const clamped = Math.min(Math.max(x, 0), 1);
		return this.polynomialHorner(clamped, this.MOBILE_COEFFS);
	}

	getQuadCenter(p1, p2, p3, p4) {
		const a1 = p3.y - p1.y;
		const b1 = p1.z - p3.z;
		const c1 = a1 * p1.z + b1 * p1.y;

		const a2 = p4.y - p2.y;
		const b2 = p2.z - p4.z;
		const c2 = a2 * p2.z + b2 * p2.y;

		const determinant = a1 * b2 - a2 * b1;

		if (determinant === 0) {
			throw new Error('Diagonals are parallel, no intersection.');
		}

		return {
			x: (p1.x + p3.x) / 2, // Approximating z by averaging diagonal endpoints (x because of x -> z switch)
			y: (a1 * c2 - a2 * c1) / determinant,
			z: (b2 * c1 - b1 * c2) / determinant,
		};
	}

	scaleToBabylonSpace(cornerCoords, imageWidth, imageHeight, viewportWidth, viewportHeight) {
		const babylonCoords = [];
		for (let i = 0; i < cornerCoords.length; i++) {
			const x = cornerCoords[i].x;
			const y = cornerCoords[i].y;

			// normalize coordinates and scale to Babylon space
			const babylonX = -(x / imageWidth - 0.5) * viewportWidth;
			const babylonY = -((y / imageHeight - 0.5) * viewportHeight); // invert Y for Babylon space

			babylonCoords.push({ x: 0, y: babylonY, z: babylonX }); // assuming x = 0 for a flat plane (x -> z swap)
		}
		return babylonCoords;
	}

	calculateAngleBetweenLines(p1, p2, q1, q2) {
		const v1 = { x: p2.x - p1.x, y: p2.y - p1.y }; // calculate the direction vectors of the two lines
		const v2 = { x: q2.x - q1.x, y: q2.y - q1.y };

		const dotProduct = v1.x * v2.x + v1.y * v2.y; // calculate the dot product of the vectors
		const magnitudeV1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y); // calculate the magnitudes of the vectors
		const magnitudeV2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);

		if (magnitudeV1 === 0 || magnitudeV2 === 0) {
			// avoid division by zero
			throw new Error('Zero-length vector encountered.');
		}

		let cosTheta = dotProduct / (magnitudeV1 * magnitudeV2); // calculate the cosine of the angle

		cosTheta = Math.max(-1, Math.min(1, cosTheta)); // clamp the value to avoid precision issues

		const angleRadians = Math.acos(cosTheta); // calculate the angle in radians and convert to degrees
		const angleDegrees = angleRadians * (180 / Math.PI);

		return angleDegrees;
	}

	getMidpoint(point1, point2) {
		return {
			x: (point1.x + point2.x) / 2,
			y: (point1.y + point2.y) / 2,
			z: (point1.z + point2.z) / 2,
		};
	}

	getPlaneLineIntersection(planePoint1, planePoint2, planePoint3, linePoint1, linePoint2) {
		const { x: x1, y: y1, z: z1 } = planePoint1;
		const { x: x2, y: y2, z: z2 } = planePoint2;
		const { x: x3, y: y3, z: z3 } = planePoint3;

		const v1 = { x: x2 - x1, y: y2 - y1, z: z2 - z1 }; // compute two vectors on the plane
		const v2 = { x: x3 - x1, y: y3 - y1, z: z3 - z1 };

		const A = -(v1.y * v2.z - v1.z * v2.y); // compute the normal vector (negated cross product for left-handed system)
		const B = -(v1.z * v2.x - v1.x * v2.z);
		const C = -(v1.x * v2.y - v1.y * v2.x);
		const D = -(A * x1 + B * y1 + C * z1);

		const { x: xL1, y: yL1, z: zL1 } = linePoint1;
		const { x: xL2, y: yL2, z: zL2 } = linePoint2;

		const dx = xL2 - xL1; // parametric line equation components
		const dy = yL2 - yL1;
		const dz = zL2 - zL1;

		const denominator = A * dx + B * dy + C * dz; // solve for t in Ax + By + Cz + D = 0

		if (denominator === 0) {
			throw new Error('The line is parallel to the plane or lies within it; no unique intersection.');
		}

		const t = -(A * xL1 + B * yL1 + C * zL1 + D) / denominator;

		return {
			// compute intersection point
			x: xL1 + t * dx,
			y: yL1 + t * dy,
			z: zL1 + t * dz,
		};
	}

	getLineLength(point1, point2) {
		return Math.sqrt(Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2) + Math.pow(point2.z - point1.z, 2));
	}
}
